import React, { useState } from 'react';
import { Box, IconButton, Paper, ClickAwayListener, Tooltip, Badge } from '@mui/material';
import { motion } from 'framer-motion';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

/**
 * StandardFloatingToolbar
 *
 * Collapses a set of list-view controls (Quick Filters, Search, Filters, Sort)
 * into a single draggable icon button. Clicking it expands a floating panel
 * containing the collapsed controls; clicking again, clicking the close icon,
 * or clicking away collapses it back down. Dragging the button (collapsed) or
 * the panel header (expanded) repositions the whole assembly together.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.quickFilterComponent - Quick filters content, rendered above the search/filter/sort content when present.
 * @param {React.ReactNode} props.searchFilterSortComponent - Search/Filters/Sort content.
 * @param {number} props.activeCount - Number of active filters/sort applied; shows a badge dot on the collapsed icon when > 0.
 */
const isFloatingToolbarOverlayClick = (event) => {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest(
      '.MuiPopover-root, .MuiMenu-root, .MuiModal-root, .MuiPickersPopper-root, .MuiAutocomplete-popper'
    )
  );
};

const StandardFloatingToolbar = ({
  quickFilterComponent = null,
  searchFilterSortComponent = null,
  activeCount = 0,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleToggle = () => setExpanded((prev) => !prev);
  const handleCollapse = () => setExpanded(false);

  const handleClickAway = (event) => {
    if (isFloatingToolbarOverlayClick(event)) return;
    handleCollapse();
  };

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <motion.div
        drag={!expanded}
        dragMomentum={false}
        dragElastic={0}
        whileDrag={{ cursor: 'grabbing' }}
        style={{ position: 'relative' }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box sx={{ position: 'relative' }}>
            <Tooltip title="Search, Filters & Sort" placement="bottom-start" disableHoverListener={expanded}>
              <Badge color="primary" variant="dot" overlap="circular" invisible={activeCount === 0}>
                <IconButton
                  onClick={handleToggle}
                  aria-label={expanded ? 'Collapse search, filters & sort' : 'Expand search, filters & sort'}
                  sx={{
                    backgroundColor: expanded ? '#EBEEFF' : '#fff',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    width: '36px',
                    height: '36px',
                    '&:hover': {
                      backgroundColor: '#EBEEFF',
                    },
                  }}
                >
                  <TuneIcon sx={{ color: expanded ? '#1D3DFC' : '#374151', fontSize: 20 }} />
                </IconButton>
              </Badge>
            </Tooltip>

            {expanded && (
              <Paper
                elevation={6}
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  width: '520px',
                  maxWidth: '95vw',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  borderRadius: '10px',
                  padding: '12px',
                  zIndex: 1250,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    cursor: 'grab',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9CA3AF' }}>
                    <DragIndicatorIcon fontSize="small" />
                    <Box component="span" sx={{ fontSize: '0.975rem', fontWeight: 600, color: '#374151' }}>
                      Search, Filters & Sort
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={handleCollapse} aria-label="Close">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                {quickFilterComponent && (
                  <Box sx={{ marginBottom: '12px' }}>
                    {quickFilterComponent}
                  </Box>
                )}

                {searchFilterSortComponent}
              </Paper>
            )}
          </Box>
        </ClickAwayListener>
      </motion.div>
    </Box>
  );
};

export default StandardFloatingToolbar;
